import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, SearchField, SortDirection, Table } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import { observer } from 'mobx-react'

import { ExplotableList } from 'modules/core/components/GroupingExplotable'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import { employeeUser } from 'modules/core/store/collections/users'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employeesCollection from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { employeeJobRole } from 'modules/job_catalog/store/collections/roles'
import type { PolicyTarget } from 'modules/permissions/store/collections/policyAuthorizations'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import TeamsMembersModal from 'modules/teams/components/MembersModal'
import membershipsCollection from 'modules/teams/store/collections/memberships'
import { membershipEmployee } from 'modules/teams/store/membershipEmployee'
import { membershipIsLead } from 'modules/teams/store/membershipIsLead'
import type { ITeam } from 'modules/teams/store/models/Team'
import { teamMemberships } from 'modules/teams/store/teamMemberships'

import BlankState from './BlankState'
import ExpandableGroup from './ExpandableGroup'
import TableHeader from './_Header'
import Item from './_Item'

interface Props {
  team: ITeam
  readonly?: boolean
  contractVersions: ContractVersions
}

const useSortedEmployees = createComputed(
  (
    employees: Employee[],
    extractorFn: (employee: Employee) => unknown,
    direction: SortDirection
  ) => {
    const sort = (employee: Employee) => {
      return extractorFn(employee)
    }
    return _.orderBy(employees, sort, [direction])
  }
)

const filteredBySearchBox = createComputed((employees: Employee[], search: string) =>
  employees.filter((target) => matchText(search, employeeUser(target).fullName()))
)

type EmployeesPerRole = Record<number | string, Employee[]>
const useGetEmployeesPerJobRole = createComputed(
  (employees: Employee[], contractVersions: ContractVersions) => {
    return employees.reduce<EmployeesPerRole>((acc, employee) => {
      const role = employeeJobRole(contractVersions, employee)
      const key = role ? role.id : 'undefined'
      const currentEmployees = acc[key] || []
      return {
        ...acc,
        ...{
          [key]: [...currentEmployees, employee],
        },
      }
    }, {})
  }
)

const employeeToPolicyTarget = (employee: Employee): PolicyTarget => {
  return {
    id: employee.attributes.id,
    policyTargetClass: '::Employee',
  }
}

const TeamList = ({ team, readonly, contractVersions }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = React.useState(false)
  const memberships = _.filter(
    teamMemberships(membershipsCollection, employeesCollection, team.id),
    (membership) =>
      !membershipIsLead(employeesCollection, membership) &&
      !membershipEmployee(employeesCollection, membership).isTerminated
  )

  useAsyncData(
    () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.create_job'),
        policyAuthorizations.authorizePolicies(
          'contracts.assign_jobs',
          (employeesPerJobRole['undefined'] || []).map(employeeToPolicyTarget)
        ),
      ]),
    []
  )

  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')

  const employees = memberships.map((membership) =>
    membershipEmployee(employeesCollection, membership)
  )

  const sortedEmployees = useSortedEmployees(
    employees,
    (employee) => {
      const user = employeeUser(employee)
      return user.firstName
    },
    'asc'
  ).get()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const filteredEmployees = filteredBySearchBox(sortedEmployees, debouncedSearch).get()
  const employeesPerJobRole = useGetEmployeesPerJobRole(filteredEmployees, contractVersions).get()
  const tableOptions = () => (
    <Box width="full">
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  const roles = _.uniq(
    filteredEmployees.map((employee) => employeeJobRole(contractVersions, employee))
  )

  const unnasignedRoleEmployeesIds = employeesPerJobRole['undefined']
    ?.filter((x) => policyAuthorizations.for('contracts.assign_jobs', x))
    .map((x) => x.attributes.id)

  const handleBulkEdit = () => {
    navigate(
      `${navigation.employees.jobCatalog.bulkEdit.fullPath()}?employeesIds=[${unnasignedRoleEmployeesIds}]&state=unnasigned`
    )
    track(trackingEvents.teams.createJobThroughTeamList)
  }

  const rolesGroups = roles.map((x) => {
    const id = x?.attributes.id
    const key = id ?? 'undefined'
    const employees = employeesPerJobRole[key] ?? []
    if (!filteredEmployees.length) {
      return null
    }

    return (
      <ExpandableGroup
        key={id}
        id={id}
        count={employees.length}
        name={x?.attributes?.name ?? i18n.t('teams.unassigned_role')}
        onBulkEdit={handleBulkEdit}
        canAssingJobs={!!unnasignedRoleEmployeesIds?.length && canCreateJob}
        teamId={team.id}
      >
        {employees.map((employee) => {
          return (
            <Item
              key={employee.id}
              employee={employee}
              team={team}
              readonly={readonly}
              contractVersions={contractVersions}
            />
          )
        })}
      </ExpandableGroup>
    )
  })

  return (
    <>
      <Table options={tableOptions} overflowY="auto" noBottomRadius fullWidth>
        <Table noRadius hideBorder>
          <TableHeader />
        </Table>
        <Table noRadius hideBorder>
          <BlankState
            show={!filteredEmployees.length}
            readonly={readonly}
            onAddMembers={() => setModalOpen(true)}
          />
          <ExplotableList>{rolesGroups}</ExplotableList>
        </Table>
      </Table>
      {modalOpen && (
        <TeamsMembersModal
          selectedGroup="members"
          onClose={() => setModalOpen(false)}
          team={team}
          contractVersions={contractVersions}
        />
      )}
    </>
  )
}

export default observer(TeamList)
